/**
 * SendScreen — Send native token or ERC-20 to an address.
 *
 * Features: address validation, token selection, amount input with max,
 * gas estimate display, confirmation modal, tx tracking.
 */

import { useState, useCallback, useEffect, type ReactNode } from 'react';
import { useWallet } from '../WalletContext.js';
import {
  formatTokenAmount,
  parseTokenAmount,
  isValidAddress,
  shortenAddress,
  explorerTxUrl,
  getChainConfig,
} from '@tokenstreaming/shared';
import type { TokenBalance } from '../types.js';
import { AddressScanner } from '../../../components/AddressScanner.js';

type SendStep = 'form' | 'scan' | 'confirm' | 'sending' | 'success' | 'error';

export function SendScreen({ onBack }: { onBack: () => void }): ReactNode {
  const { status, balances, chainId, actions } = useWallet();
  const chain = getChainConfig(chainId);

  const [step, setStep] = useState<SendStep>('form');
  const [recipient, setRecipient] = useState('');
  const [amountStr, setAmountStr] = useState('');
  const [selectedToken, setSelectedToken] = useState<string>('native');
  const [error, setError] = useState('');
  const [txHash, setTxHash] = useState('');
  const [gasEstimate, setGasEstimate] = useState<string>('');

  const selectedBalance = selectedToken === 'native'
    ? { balance: balances.native, decimals: chain.nativeDecimals, symbol: chain.nativeSymbol }
    : (() => {
        const token = balances.tokens.find(t => t.address === selectedToken);
        return token
          ? { balance: token.balance, decimals: token.decimals, symbol: token.symbol }
          : { balance: 0n, decimals: 18, symbol: '???' };
      })();

  const handleMaxAmount = useCallback(() => {
    setAmountStr(formatTokenAmount(selectedBalance.balance, selectedBalance.decimals));
  }, [selectedBalance]);

  const handleContinue = useCallback(() => {
    setError('');

    if (!isValidAddress(recipient)) {
      setError('Invalid recipient address');
      return;
    }

    if (!amountStr || amountStr === '0') {
      setError('Enter an amount');
      return;
    }

    let amount: bigint;
    try {
      amount = parseTokenAmount(amountStr, selectedBalance.decimals);
    } catch {
      setError('Invalid amount format');
      return;
    }

    if (amount <= 0n) {
      setError('Amount must be greater than 0');
      return;
    }

    if (amount > selectedBalance.balance) {
      setError('Insufficient balance');
      return;
    }

    setStep('confirm');
  }, [recipient, amountStr, selectedBalance]);

  const handleSend = useCallback(async () => {
    setStep('sending');
    try {
      const amount = parseTokenAmount(amountStr, selectedBalance.decimals);
      const tokenAddress = selectedToken === 'native' ? undefined : selectedToken;
      const hash = await actions.sendTransaction(recipient, amount, tokenAddress);
      setTxHash(hash);
      setStep('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Transaction failed');
      setStep('error');
    }
  }, [amountStr, selectedBalance.decimals, selectedToken, recipient, actions]);

  // ─── Render ───────────────────────────────────────────────────────────

  if (step === 'success') {
    return (
      <div className="screen">
        <div className="screen-body centered">
          <div className="success-icon">&#x2713;</div>
          <h3>Transaction Sent</h3>
          <p className="text-secondary">Your transaction has been submitted to the network.</p>
          <a
            href={explorerTxUrl(chain.explorerUrl, txHash)}
            target="_blank"
            rel="noopener noreferrer"
            className="tx-link"
          >
            View on {chain.name} Explorer
          </a>
          <p className="text-mono text-sm">{shortenAddress(txHash, 10)}</p>
          <button className="btn-primary btn-full" onClick={onBack}>Done</button>
        </div>
      </div>
    );
  }

  if (step === 'error') {
    return (
      <div className="screen">
        <div className="screen-body centered">
          <div className="error-icon">&#x2717;</div>
          <h3>Transaction Failed</h3>
          <p className="error-message">{error}</p>
          <button className="btn-primary btn-full" onClick={() => setStep('form')}>Try Again</button>
          <button className="btn-ghost btn-full" onClick={onBack}>Cancel</button>
        </div>
      </div>
    );
  }

  if (step === 'confirm' || step === 'sending') {
    const amount = parseTokenAmount(amountStr, selectedBalance.decimals);
    return (
      <div className="screen">
        <div className="screen-header">
          <button className="btn-ghost" onClick={() => setStep('form')} disabled={step === 'sending'}>Back</button>
          <h2>Confirm Send</h2>
        </div>
        <div className="screen-body">
          <div className="confirm-card">
            <div className="confirm-row">
              <span className="confirm-label">To</span>
              <span className="confirm-value text-mono">{shortenAddress(recipient, 8)}</span>
            </div>
            <div className="confirm-row">
              <span className="confirm-label">Amount</span>
              <span className="confirm-value">
                {formatTokenAmount(amount, selectedBalance.decimals)} {selectedBalance.symbol}
              </span>
            </div>
            <div className="confirm-row">
              <span className="confirm-label">Network</span>
              <span className="confirm-value">{chain.name}</span>
            </div>
            {gasEstimate && (
              <div className="confirm-row">
                <span className="confirm-label">Est. Gas</span>
                <span className="confirm-value">{gasEstimate} {chain.nativeSymbol}</span>
              </div>
            )}
          </div>

          <button
            className="btn-primary btn-full"
            onClick={() => void handleSend()}
            disabled={step === 'sending'}
          >
            {step === 'sending' ? 'Sending...' : 'Confirm & Send'}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'scan') {
    return (
      <AddressScanner
        onAddress={(address, amount, token) => {
          setRecipient(address);
          if (amount) setAmountStr(amount);
          if (token) setSelectedToken(token);
          setStep('form');
        }}
        onCancel={() => setStep('form')}
      />
    );
  }

  // step === 'form'
  return (
    <div className="screen">
      <div className="screen-header">
        <button className="btn-ghost" onClick={onBack}>Back</button>
        <h2>Send</h2>
      </div>
      <div className="screen-body">
        <div className="form-group">
          <label>Recipient Address</label>
          <div className="input-with-action">
            <input
              type="text"
              value={recipient}
              onChange={e => setRecipient(e.target.value)}
              placeholder="0x..."
              autoFocus
              spellCheck={false}
            />
            <button className="btn-ghost btn-sm scan-btn" onClick={() => setStep('scan')}>
              Scan QR
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>Token</label>
          <select value={selectedToken} onChange={e => setSelectedToken(e.target.value)}>
            <option value="native">{chain.nativeSymbol} (Native)</option>
            {balances.tokens.map(token => (
              <option key={token.address} value={token.address}>
                {token.symbol} — {formatTokenAmount(token.balance, token.decimals)}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>
            Amount
            <button className="btn-ghost btn-xs" onClick={handleMaxAmount}>MAX</button>
          </label>
          <input
            type="text"
            value={amountStr}
            onChange={e => setAmountStr(e.target.value)}
            placeholder="0.0"
            inputMode="decimal"
          />
          <span className="form-hint">
            Balance: {formatTokenAmount(selectedBalance.balance, selectedBalance.decimals)} {selectedBalance.symbol}
          </span>
        </div>

        {error && <div className="error-message">{error}</div>}

        <button className="btn-primary btn-full" onClick={handleContinue}>
          Continue
        </button>
      </div>
    </div>
  );
}
